import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";

import {
  InvestmentStyleDiagnosisRequest,
  InvestmentStyleDiagnosisRequestPurposesEnum as PurposeEnum,
  InvestmentStyleDiagnosisRequestMonthlyOutOfPocketEnum as MonthlyEnum,
  InvestmentStyleDiagnosisRequestHoldingPeriodEnum as HoldingEnum,
  InvestmentStyleDiagnosisRequestManagementStyleEnum as ManagementEnum,
  InvestmentStyleDiagnosisRequestShortTermSaleIntentEnum as SaleIntentEnum,
} from "@/api/generated";
import { PURPOSE_OPTIONS } from "@/constants/investmentStyleDiagnosisQuestions";
import { useInvestmentStyleFunnelEvent } from "@/hooks/useInvestmentStyleFunnelEvent";
import { Answers } from "@/types/investmentStyleDiagnosis";
import { AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { clearSavedAnswers, isCompleted, loadSavedAnswers, saveAnswers } from "@/utilities/investmentStyleDiagnosisStorage";

import { firstInvalidStepInAnswers, isAnswersStepValid } from "../utils/investmentStyleDiagnosisValidation";

// 診断の送信成功後（別タスクで useSubmitInvestmentStyleDiagnosis.ts から呼び出す）に、次回アクセス時へ
// 古い回答が復元されないよう sessionStorage をクリアするための関数。実体は investmentStyleDiagnosisStorage.ts。
export { clearSavedAnswers };

// ページ側（送信直前の全step再検証）でも使うため export する。
// 追加設問を外し、更新前と同じ12画面・設問順で進める。
export const TOTAL_STEPS = 12;

// 現在の設問番号は URL のクエリで持つ。同一ルートのまま search だけが変わるためページは再マウントされず、
// 回答をメモリに保持したまま、ブラウザバックや Android のハードウェア戻るで1問ずつ戻れる。
const STEP_QUERY_KEY = "q";

const parseStep = (search: string): number => {
  const parsed = Number(new URLSearchParams(search).get(STEP_QUERY_KEY));
  if (!Number.isInteger(parsed) || parsed < 0 || parsed >= TOTAL_STEPS) return 0;
  return parsed;
};

// 設問ごとの到達計測。どの設問で離脱したかを見るため、ボタン押下ではなく設問の表示時に送る。
// キーは画面の順番。画面を挿入したら既存のイベントが別の設問に紐づかないよう必ず振り直す。
export const STEP_EVENT_MAP: Partial<Record<number, AppEventTokenEnum>> = {
  0: AppEventTokenEnum.InvestmentStyleDiagnosisPurposeAccess,
  1: AppEventTokenEnum.InvestmentStyleDiagnosisInterestAccess,
  2: AppEventTokenEnum.InvestmentStyleDiagnosisSuccessAccess,
  3: AppEventTokenEnum.InvestmentStyleDiagnosisMonthlyAccess,
  4: AppEventTokenEnum.InvestmentStyleDiagnosisHoldingAccess,
  5: AppEventTokenEnum.InvestmentStyleDiagnosisLocationYieldAccess,
  6: AppEventTokenEnum.InvestmentStyleDiagnosisManagementAccess,
  7: AppEventTokenEnum.InvestmentStyleDiagnosisSaleTimingAccess,
  8: AppEventTokenEnum.InvestmentStyleDiagnosisMeetingItemsAccess,
  9: AppEventTokenEnum.InvestmentStyleDiagnosisExplanationAccess,
  10: AppEventTokenEnum.InvestmentStyleDiagnosisAnxietyAccess,
  11: AppEventTokenEnum.InvestmentStyleDiagnosisAvoidAccess,
};

export const INITIAL_ANSWERS: Answers = {
  purposes: [],
  primary_purpose: null,
  ideal_text: "",
  target_monthly_income: null,
  interest: [],
  success_conditions: [],
  monthly_out_of_pocket: null,
  borrowing_stance: null,
  holding_period: null,
  location_yield_preference: null,
  management_style: null,
  short_term_sale_intent: null,
  rental_income_use: null,
  work_outlook: null,
  first_purchase_timing: null,
  planner_comparison_count: null,
  meeting_check_items: [],
  explanation_styles: [],
  meeting_free_text: "",
  anxiety: {},
  avoid_sales_styles: [],
};

interface UseInvestmentStyleDiagnosisForm {
  step: number;
  answers: Answers;
  update: <K extends keyof Answers>(key: K, value: Answers[K]) => void;
  handlePurposesChange: (next: PurposeEnum[]) => void;
  primaryOptions: typeof PURPOSE_OPTIONS;
  isStepValid: (index: number) => boolean;
  // 0 〜 upToExclusive-1 の中で最初に isStepValid を満たさない step を返す（全て有効なら null）。
  // 送信直前の全step再検証（DiagnosisInvestmentStylePage.handleNext）で使う。
  firstInvalidStep: (upToExclusive: number) => number | null;
  // 任意の step へ強制的に遷移する。送信直前ガードで、未入力の step へユーザーを差し戻すために使う。
  goToStep: (index: number) => void;
  buildRequest: () => InvestmentStyleDiagnosisRequest;
  remaining: number;
  isLastStep: boolean;
  goNext: () => void;
  // true の間は結果画面へのリダイレクト中であることを示す。ページ側はこの間フォームを描画してはいけない
  // （完了直後にブラウザバックでこのルートへ戻ってきたケースを、フォームを一切ちらつかせずに結果画面へ誘導するため）。
  isRedirectingToResult: boolean;
}

export const useInvestmentStyleDiagnosisForm = (): UseInvestmentStyleDiagnosisForm => {
  const history = useHistory();
  const location = useLocation();
  // マウント時に一度だけ sessionStorage の完了フラグを読む。true なら、完了直後にブラウザバック等で
  // このルートへ戻ってきたセッションなので、フォームを一切描画せず結果画面へリダイレクトする
  // （下の useLayoutEffect で実際のナビゲーションを行う。ここでは初回レンダーから判定を確定させたいため
  // 遅延初期化の ref で1回だけ評価する）。
  const isCompletedRef = useRef<boolean>();
  if (isCompletedRef.current === undefined) {
    isCompletedRef.current = isCompleted();
  }
  const isRedirectingToResult = isCompletedRef.current;

  // マウント時に一度だけ sessionStorage を読み、以降の判定（下の useLayoutEffect）にも使い回す。
  // 通常の useRef(loadSavedAnswers()) だと式が毎レンダー評価されてしまうため、遅延初期化で1回に絞る。
  const savedAnswersRef = useRef<Answers | null>();
  if (savedAnswersRef.current === undefined) {
    savedAnswersRef.current = loadSavedAnswers();
  }
  const [answers, setAnswers] = useState<Answers>(() => savedAnswersRef.current ?? INITIAL_ANSWERS);
  const step = parseStep(location.search);

  // フロー内の設問遷移（進行・差し戻し・q の正規化）は必ずここを通す。
  // history.push / replace に { pathname, search } だけを渡すと遷移先エントリの state が失われ、
  // 入口情報（fromHome）が設問を1つ進めた時点で消えてしまうため、現在エントリの state を常に載せ替える
  // （完了済みリダイレクトの useLayoutEffect が location.state を引き継いでいるのと同じ理由）。
  const navigateToSearch = (search: string, options?: { replace?: boolean }) => {
    const to = { pathname: location.pathname, search, state: location.state };
    if (options?.replace) {
      history.replace(to);
    } else {
      history.push(to);
    }
  };

  // 完了済みセッションでの再訪問を検知したら、結果画面へ差し戻す。paint 前に走る useLayoutEffect にすることで、
  // フォームの初期stepが一瞬でも描画されるのを防ぐ（ページ側は isRedirectingToResult の間 null を返す）。
  // 他の useLayoutEffect より前に置き、以降の副作用（sessionStorage復元やstep再検証）が
  // 無駄な history.replace を重ねて呼ばないようにしている。
  useLayoutEffect(() => {
    if (!isRedirectingToResult) return;
    // 入口の state（fromHome）を結果画面の履歴エントリへ引き継ぐ
    history.replace("/investment_style_diagnosis/result", location.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback(<K extends keyof Answers>(key: K, value: Answers[K]) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  // 回答の変更を都度 sessionStorage へ保存する。リロードや直リンクからの再開に使う。
  // リダイレクト中は submit 時に clearSavedAnswers 済みの STORAGE_KEY を
  // 空の INITIAL_ANSWERS で書き戻してしまわないよう何もしない。
  useEffect(() => {
    if (isRedirectingToResult) return;
    saveAnswers(answers);
  }, [answers, isRedirectingToResult]);

  // 保存済みの回答が復元できた場合は、リロードや直リンクからの再開とみなし URL の q をそのまま維持する
  // （設問番号自体は parseStep で 0〜11 にクランプ・検証されるのでここでは触らなくてよい）。
  // 保存済みの回答が無い場合（＝真の初回アクセス）だけ q を 0 へ強制する。
  // 設問の行き来では再マウントされないので、この判定はマウント時の1回だけ走る。
  // 誤った設問が一瞬描画されるのを防ぐため、paint 前に走る useLayoutEffect で URL を戻す。
  useLayoutEffect(() => {
    if (isRedirectingToResult) return;
    if (savedAnswersRef.current !== null) return;
    if (parseStep(location.search) === 0) return;
    navigateToSearch("", { replace: true });
  }, []);

  // 戻る操作で同じ設問を再表示しても到達計測が二重に飛ばないよう、送信済みの設問を覚えておく。
  // リダイレクト中はその設問に実際に到達したわけではないため計測しない。
  const sentStepsRef = useRef<Set<number>>(new Set());
  const stepEventToken =
    isRedirectingToResult || sentStepsRef.current.has(step) ? undefined : STEP_EVENT_MAP[step];
  useInvestmentStyleFunnelEvent(stepEventToken);
  useEffect(() => {
    if (!stepEventToken) return;
    sentStepsRef.current.add(step);
  }, [stepEventToken, step]);

  // Q1-1で選択した目的だけを最重要の候補にする。選択から外れたら最重要も解除。
  const primaryOptions = useMemo(
    () => PURPOSE_OPTIONS.filter((option) => answers.purposes.includes(option.value)),
    [answers.purposes]
  );
  const handlePurposesChange = (next: PurposeEnum[]) => {
    setAnswers((prev) => ({
      ...prev,
      purposes: next,
      primary_purpose: prev.primary_purpose && next.includes(prev.primary_purpose) ? prev.primary_purpose : null,
    }));
  };

  const isStepValid = (index: number): boolean => isAnswersStepValid(answers, index);
  const firstInvalidStep = (upToExclusive: number): number | null =>
    firstInvalidStepInAnswers(answers, upToExclusive);

  // 修正A: 現在の step より前（0 〜 step-1）が全て valid かを step が変わるたびに再検証するガード。
  // goNext（「次へ」ボタン）以外の経路 ―― ブラウザの戻る/進む、URL 直接編集など ―― で history が
  // 動いた場合も、q が変わればこの effect が走る。無効な step が見つかれば、その中で最初に無効な
  // step へ強制的に差し戻す。「最大到達step」のような別のガード変数は持たず、都度「今いるstepより
  // 前が全部validか」を判定するだけにすることで、goNext を経由しないあらゆる遷移経路を一律にカバーする。
  // 誤った設問が一瞬描画されるのを防ぐため、paint 前に走る useLayoutEffect にしている
  // （マウント時の直前の useLayoutEffect による sessionStorage 復元後の再判定にもなるが、
  // 復元された回答がその時点で有効であれば何もしないため、正常な再開フローは妨げない）。
  useLayoutEffect(() => {
    if (isRedirectingToResult) return;
    const invalidStep = firstInvalidStepInAnswers(answers, step);
    if (invalidStep === null) return;
    navigateToSearch(`?${STEP_QUERY_KEY}=${invalidStep}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // 送信直前の最終防衛線（修正B）から、無効な step へユーザーを差し戻すために使う。
  const goToStep = (index: number) => {
    navigateToSearch(`?${STEP_QUERY_KEY}=${index}`);
  };

  const buildRequest = (): InvestmentStyleDiagnosisRequest => ({
    purposes: answers.purposes,
    // primary_purpose は Purposes と同一の文字列値だが enum 型が異なるため unknown 経由でキャスト
    primary_purpose: answers.primary_purpose as unknown as InvestmentStyleDiagnosisRequest["primary_purpose"],
    ideal_text: answers.ideal_text.trim() || null,
    interest_1st: answers.interest[0],
    // interest_2nd/3rd は 1st と同一の文字列値だが別 enum 型として生成されるためキャスト
    interest_2nd: answers.interest[1] as unknown as InvestmentStyleDiagnosisRequest["interest_2nd"],
    interest_3rd: answers.interest[2] as unknown as InvestmentStyleDiagnosisRequest["interest_3rd"],
    success_conditions: answers.success_conditions,
    monthly_out_of_pocket: answers.monthly_out_of_pocket as MonthlyEnum,
    holding_period: answers.holding_period as HoldingEnum,
    location_yield_preference: answers.location_yield_preference as number,
    management_style: answers.management_style as ManagementEnum,
    short_term_sale_intent: answers.short_term_sale_intent as SaleIntentEnum,
    meeting_check_items: answers.meeting_check_items,
    explanation_styles: answers.explanation_styles,
    meeting_free_text: answers.meeting_free_text.trim() || null,
    anxiety_loan: answers.anxiety.anxiety_loan as unknown as InvestmentStyleDiagnosisRequest["anxiety_loan"],
    anxiety_vacancy: answers.anxiety.anxiety_vacancy as unknown as InvestmentStyleDiagnosisRequest["anxiety_vacancy"],
    anxiety_exit: answers.anxiety.anxiety_exit as unknown as InvestmentStyleDiagnosisRequest["anxiety_exit"],
    anxiety_repair: answers.anxiety.anxiety_repair as unknown as InvestmentStyleDiagnosisRequest["anxiety_repair"],
    anxiety_sales_pressure:
      answers.anxiety.anxiety_sales_pressure as unknown as InvestmentStyleDiagnosisRequest["anxiety_sales_pressure"],
    avoid_sales_styles: answers.avoid_sales_styles,
  });

  const remaining = TOTAL_STEPS - step;
  const isLastStep = step === TOTAL_STEPS - 1;
  const goNext = () => {
    navigateToSearch(`?${STEP_QUERY_KEY}=${step + 1}`);
  };

  return {
    step,
    answers,
    update,
    handlePurposesChange,
    primaryOptions,
    isStepValid,
    firstInvalidStep,
    goToStep,
    buildRequest,
    remaining,
    isLastStep,
    goNext,
    isRedirectingToResult,
  };
};
